import React, { useEffect, useMemo, useState } from "react";
import "../../../index.css";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Hotel from "../../../helper/images/hotel.png";
import Hotel2 from "../../../helper/images/hotel2.png";
import Hotel3 from "../../../helper/images/hotel3.png";
import { RiHotelLine } from "react-icons/ri";
import { IoArrowBackOutline, IoArrowForward } from "react-icons/io5";
import { getRestaurantsOrHotels } from "../../../api/user";
import { useQuery } from "@tanstack/react-query";
import { MdStar, MdStarHalf, MdStarOutline } from "react-icons/md";
import { useSettings } from "../../../context/SettingsContext";
import { useNavigate } from "react-router-dom";
import { useLocationData } from "../../../context/locationContext";
import { useTranslation } from "../../../hooks/useTranslation";
import { CardLoading } from "../../../helper/lorder";

const DEFAULT_FILTERS = {
  location: "",
  category: "Restaurants",
  minPrice: "",
  maxPrice: "",
  ratings: "Any rating",
  amenities: "All amenities",
};

const SearchFilter = () => {
  // const [userLocation, setUserLocation] = useState({ latitude: null, longitude: null });
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const { settings: hotelSettings } = useSettings();
  const showHotels = hotelSettings?.showHotels;
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const navigate = useNavigate();
  const { userLocation, setUserLocation } = useLocationData();
  const { t } = useTranslation();
  const copy = t("home").searchFilter;
  const emptyStateTitle =
    copy?.noResultsTitle || "No matching spots just yet";
  const emptyStateDescription =
    copy?.noResultsDescription ||
    "Try adjusting your filters or explore a nearby city to discover more stays and restaurants.";
  const emptyStateCta =
    copy?.refreshCta || "Clear filters & refresh";

  const isHotel = appliedFilters.category === "Hotels";

  const mapRatingToNumber = (ratingString) => {
    switch (ratingString) {
      case "5 stars only":
        return 5;
      case "4 stars & above":
        return 4;
      case "3 stars & above":
        return 3;
      case "Any rating":
      default:
        return undefined;
    }
  };

  const {
    data: searchedData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["hotels-restaurants", appliedFilters, userLocation],
    queryFn: () =>
      getRestaurantsOrHotels({
        hotels: isHotel,
        rating: mapRatingToNumber(appliedFilters.ratings),
        address: appliedFilters.location,
        features:
          appliedFilters.amenities === "All amenities"
            ? undefined
            : appliedFilters.amenities,
        min: appliedFilters.minPrice,
        max: appliedFilters.maxPrice,
        latitude: userLocation.latitude,
        longitude: userLocation.longitude,
      }),
    keepPreviousData: true,
  });

  const data = searchedData?.data?.data;

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          console.error("Location error:", error);
        }
      );
    } else {
      console.error("Geolocation is not supported by this browser.");
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    const formattedValue =
      name === "amenities"
        ? value
            .split("+")
            .map((v) => v.trim())
            .join(",")
        : value;

    setFilters((prev) => ({
      ...prev,
      [name]: formattedValue,
    }));
  };

  const handleSearch = () => {
    setAppliedFilters(filters);
  };

  const handleResetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
  };

  const handleNavigate = (id) => {
    if (isHotel) {
      navigate(`/hoteldetail`, {
        state: { hotelId: id },
      });
    } else {
      navigate(`/restaurantdetail`, {
        state: { restaurantId: id },
      });
    }
  };

  const NextArrow = (props) => {
    const { className, style, onClick } = props;
    const customClassName = `absolute top-[50%] right-[-40px] z-10 cursor-pointer bg-[#DEDEDD] hover:bg-[#FFA808] hover:text-white text-black rounded-full w-[40px] h-[40px] flex items-center justify-center text-black text-[20px]`;
    return (
      <div className={customClassName} onClick={onClick}>
        <IoArrowForward />
      </div>
    );
  };

  const PrevArrow = (props) => {
    const { className, style, onClick } = props;
    const customClassName = `absolute top-[50%] left-[-40px] z-10 cursor-pointer bg-[#DEDEDD] hover:bg-[#FFA808] hover:text-white text-black rounded-full w-[40px] h-[40px] flex items-center justify-center text-[#B8BDBB] hover:text-black text-[20px]`;
    return (
      <div className={customClassName} onClick={onClick}>
        <IoArrowBackOutline />
      </div>
    );
  };
  var settings = {
    dots: false,
    arrows: true,
    infinite: data?.length > 2 ? true : false,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    pauseOnHover: false,
    cssEase: "linear",
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    responsive: [
      {
        breakpoint: 1399,
        settings: {
          slidesToShow: 3,
          infinite: true,
          dots: true,
          arrows: false,
        },
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          infinite: true,
          dots: true,
          arrows: false,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          dots: true,
          arrows: false,
        },
      },
      {
        breakpoint: 575,
        settings: {
          slidesToShow: 1,
          dots: true,
          arrows: false,
        },
      },
    ],
  };

  // const featureOptions = [
  //   { value: "wifi", label: t("home").searchFilter.amenities.wifi },
  //   { value: "parking", label: t("home").searchFilter.amenities.parking },
  //   {
  //     value: "outdoor_seating",
  //     label: t("home").searchFilter.amenities.outdoor_seating,
  //   },
  //   { value: "live_music", label: t("home").searchFilter.amenities.live_music },
  //   {
  //     value: "pet_friendly",
  //     label: t("home").searchFilter.amenities.pet_friendly,
  //   },
  //   {
  //     value: "kid_friendly",
  //     label: t("home").searchFilter.amenities.kid_friendly,
  //   },
  //   {
  //     value: "vegan_options",
  //     label: t("home").searchFilter.amenities.vegan_options,
  //   },
  //   {
  //     value: "wheelchair_accessible",
  //     label: t("home").searchFilter.amenities.wheelchair_accessible,
  //   },
  //   {
  //     value: "reservations",
  //     label: t("home").searchFilter.amenities.reservations,
  //   },
  //   { value: "takeaway", label: t("home").searchFilter.amenities.takeaway },
  //   { value: "delivery", label: t("home").searchFilter.amenities.delivery },
  //   { value: "buffet", label: t("home").searchFilter.amenities.buffet },
  //   { value: "bar", label: t("home").searchFilter.amenities.bar },
  //   {
  //     value: "private_dining",
  //     label: t("home").searchFilter.amenities.private_dining,
  //   },
  //   {
  //     value: "smoking_area",
  //     label: t("home").searchFilter.amenities.smoking_area,
  //   },
  //   {
  //     value: "swimming_pool",
  //     label: t("home").searchFilter.amenities.swimming_pool,
  //   },
  //   // {
  //   //   value: "fitness_center",
  //   //   label: t("home").searchFilter.amenities.fitness_center,
  //   // },
  //   // { value: "spa", label: t("home").searchFilter.amenities.spa },
  //   {
  //     value: "air_conditioning",
  //     label: t("home").searchFilter.amenities.air_conditioning,
  //   },
  // ];

  const translat = t("newrestaurantadd").featureOptions;
  const featureOptions = [
    { value: "wifi", label: `${translat.wifi}` },
    // { value: "Parking Available", label: `${translat.parkingAvailable}` },
    { value: "Outdoor Seating", label: `${translat.outdoorSeating}` },
    { value: "Live Music", label: `${translat.liveMusic}` },
    { value: "Pet Friendly", label: `${translat.petFriendly}` },
    { value: "Kid Friendly", label: `${translat.kidFriendly}` },
    { value: "Vegan Options", label: `${translat.veganOptions}` },
    {
      value: "Wheelchair Accessible",
      label: `${translat.wheelchairAccessible}`,
    },
    { value: "Accepts Reservations", label: `${translat.acceptsReservations}` },
    { value: "Takeaway Available", label: `${translat.takeawayAvailable}` },
    { value: "Home Delivery", label: `${translat.homeDelivery}` },
    { value: "Buffet Service", label: `${translat.buffetService}` },
    // { value: "Bar Available", label: `${translat.barAvailable}` },
    { value: "Private Dining", label: `${translat.privateDining}` },
    // { value: "Smoking Area", label: `${translat.smokingArea}` },
  ];

  return (
    <>
      <div className="container">
        <div className="lg:py-[100px] md:py-[70px] py-[30px]">
          <h2 className="text-center text-[#18181B] lg:text-[40px] md:text-[30px] text-[25px] font-semibold lg:leading-[47px] leading-9 md:mb-[30px] mb-6">
            {showHotels
              ? t("home").searchFilter.titleWithHotels
              : t("home").searchFilter.titleWithoutHotels}
          </h2>
          <div className="md:p-[33px] md:pb-[47px] p-5 bg-white rounded-[10px] shadow-filter max-w-[1231px] mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div>
                <label className="text-base text-[#686868] mb-[10px] block">
                  {t("home").searchFilter.locationLabel}
                </label>
                <input
                  type="text"
                  name="location"
                  value={filters.location}
                  onChange={handleChange}
                  placeholder={t("home").searchFilter.locationPlaceholder}
                  className="border border-[#DDDDDD] rounded-full p-5 w-full placeholder:text-[#686868] placeholder:text-lg focus:outline-none focus:border-[#4CAF50]"
                />
              </div>
              <div>
                <label className="text-base text-[#686868] mb-[10px] block">
                  {t("home").searchFilter.categoryLabel}
                </label>
                <select
                  name="category"
                  value={filters.category}
                  onChange={handleChange}
                  className="border border-[#DDDDDD] rounded-full p-5 w-full placeholder:text-[#686868] placeholder:text-lg focus:outline-none focus:border-[#4CAF50]"
                >
                  <option>{t("home").searchFilter.categoryRestaurants}</option>
                  {showHotels && (
                    <option>{t("home").searchFilter.categoryHotels}</option>
                  )}
                </select>
              </div>
              <div>
                <label className="text-base text-[#686868] mb-[10px] block">
                  {t("home").searchFilter.priceRangeLabel}
                </label>
                <div className="flex gap-2 items-center border border-[#DDDDDD] rounded-full  w-full placeholder:text-[#686868] placeholder:text-lg focus:outline-none focus:border-[#4CAF50]">
                  <span className="border-r border-[#ddd] px-[16px] py-[18px]">
                    ₾ 
                  </span>
                  <input
                    type="number"
                    name="minPrice"
                    value={filters.minPrice}
                    onChange={handleChange}
                    className="w-full outline-none"
                  />
                  <span className="border-x border-[#ddd] px-[16px] py-[18px]">
                    ₾ 
                  </span>
                  <input
                    type="number"
                    name="maxPrice"
                    value={filters.maxPrice}
                    onChange={handleChange}
                    className="w-full outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="text-base text-[#686868] mb-[10px] block">
                  {t("home").searchFilter.ratingsLabel}
                </label>
                <select
                  name="ratings"
                  value={filters.ratings}
                  onChange={handleChange}
                  className="border border-[#DDDDDD] rounded-full p-5 w-full placeholder:text-[#686868] placeholder:text-lg focus:outline-none focus:border-[#4CAF50]"
                >
                  <option>{t("home").searchFilter.rating5}</option>
                  <option>{t("home").searchFilter.rating4}</option>
                  <option>{t("home").searchFilter.rating3}</option>
                  <option>{t("home").searchFilter.ratingAny}</option>
                </select>
              </div>

              <div>
                <label className="text-base text-[#686868] mb-[10px] block">
                  {t("home").searchFilter.amenitiesLabel}
                </label>
                <select
                  name="amenities"
                  value={filters.amenities}
                  onChange={handleChange}
                  className="border border-[#DDDDDD] rounded-full p-5 w-full placeholder:text-[#686868] placeholder:text-lg focus:outline-none focus:border-[#4CAF50]"
                >
                  {featureOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                  <option value="">All Amenities</option>
                </select>
              </div>

              {/* <div>
                <label className="text-base text-[#686868] mb-[10px] block">
                  Amenities
                </label>
                <select
                  name="amenities"
                  value={filters.amenities}
                  onChange={handleChange}
                  className="border border-[#DDDDDD] rounded-full p-5 w-full placeholder:text-[#686868] placeholder:text-lg focus:outline-none focus:border-[#4CAF50]"
                >
                  <option>Free Wi-Fi + Swimming Pool</option>
                  <option>Parking + Gym</option>
                  <option>Pet-friendly</option>
                  <option>All amenities</option>

                </select>
              </div> */}
            </div>

            <div className="text-center mt-[30px]">
              <button
                onClick={handleSearch}
                className=" md:w-auto w-full rounded-[2px] bg-[#4CAF50] 2xl:text-[20px] text-base text-white text-center font-bold 2xl:p-[14px] 2xl:px-[41px] p-3 green-shadow hover:bg-[#209b25]"
              >
                {copy.searchButton}
              </button>
            </div>
          </div>

          {/* ================Filter serach slider ================ */}
          {isLoading ? (
            <CardLoading />
          ) : data?.length ? (
            <div className="pt-[37px] filter-slider">
              <Slider {...settings}>
                {data.map((item) => (
                  <div
                    key={item?._id}
                    onClick={() => handleNavigate(item?._id)}
                    className="border border-[#D9D9D9] rounded-[10px] cursor-pointer"
                  >
                    <div>
                      <img
                        src={`${item?.images?.[0]}`}
                        alt="hotel"
                        className="w-full h-[284.65px] object-cover rounded-t-[9px]"
                      />
                    </div>
                    <div className="pt-[14px] pb-[23px] pr-[17px] pl-[10px]">
                      <h2 className="text-lg font-semibold leading-[30px] mb-[5px] text-[#18181B]">
                        {item?.name}
                      </h2>
                      <div className="flex items-center gap-[10px] justify-between flex-wrap">
                        <span className="text-base leading-[26px] text-[#18181B]">
                          {item?.address}
                        </span>
                        <div className="flex items-center gap-[2px]">
                          {Array(Math.floor(item?.rating))
                            .fill()
                            .map((_, index) => (
                              <MdStar
                                key={`full-${index}`}
                                size={21}
                                className="text-[#FF9D00]"
                              />
                            ))}

                          {item?.rating % 1 !== 0 && (
                            <MdStarHalf size={21} className="text-[#FF9D00]" />
                          )}

                          {Array(5 - Math.ceil(item?.rating))
                            .fill()
                            .map((_, index) => (
                              <MdStarOutline
                                key={`empty-${index}`}
                                size={21}
                                className="text-[#FF9D00]"
                              />
                            ))}
                        </div>
                      </div>
                      <div className="flex gap-[10px] items-center mt-[11px]">
                        <RiHotelLine size={24} className="text-[#7B7B7B]" />
                        <span className="text-[15px] leading-[30px] text-[#18181B]">
                          {t("home").searchFilter.hotelStarsExample}
                        </span>
                      </div>
                      <p className="text-[15px] text-[#4CAF50] semibold mt-[10px]">
                        {item?.distance
                          ? `${item?.distance} ${
                              t("home").searchFilter.distanceText
                            }`
                          : null}
                      </p>
                    </div>
                  </div>
                ))}
              </Slider>
            </div>
          ) : (
            <div className="pt-14 pb-10 text-center bg-[#F7FAF7] rounded-[20px] border border-[#E5F2E4] mt-6">
              <p className="text-lg font-semibold text-[#1B7200]">
                {emptyStateTitle}
              </p>
              <p className="text-base text-[#4B5563] max-w-2xl mx-auto mt-2">
                {emptyStateDescription}
              </p>
              <button
                onClick={handleResetFilters}
                className="mt-6 px-6 py-2 rounded-full border border-[#4CAF50] text-[#1B7200] font-semibold hover:bg-white"
              >
                {emptyStateCta}
              </button>
            </div>
          )}
          
          
        </div>
      </div>
    </>
  );
};

export default SearchFilter;
